import Layout from "../components/Layout";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FaCheck,
  FaClock,
  FaExclamationCircle,
  FaCalendarAlt,
  FaTasks,
  FaFileAlt,
  FaChartLine
} from "react-icons/fa";

const rawBackendUrl = process.env.REACT_APP_BACKEND_URL || "";
const backend_url = rawBackendUrl.startsWith("http://") || rawBackendUrl.startsWith("https://") ? rawBackendUrl : `https://${rawBackendUrl}`;
const API_URL = `${backend_url}/api`;
const getToken = () => localStorage.getItem("access_token");
const getAuthData = () => JSON.parse(localStorage.getItem("auth_data") || "{}");

/* ================= KPI CARD ================= */
function KpiCard({ label, value, color, delay, icon, onClick }) {
  const [show, setShow] = useState(false);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    setTimeout(() => setShow(true), delay);
  }, [delay]);

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        ...styles.kpiCard,
        borderLeft: `4px solid ${color}`,
        opacity: show ? 1 : 0,
        transform: show
          ? hover
            ? "translateY(-6px) scale(1.03)"
            : "translateY(0)"
          : "translateY(12px)",
        boxShadow: hover
          ? "0 12px 30px rgba(94,23,235,0.25)"
          : "0 4px 12px rgba(0,0,0,0.06)",
        cursor: onClick ? "pointer" : "default",
      }}
    >
      <div style={styles.kpiHeader}>
        {icon && <span style={{ marginRight: "8px", color }}>{icon}</span>}
        <span style={styles.kpiLabel}>{label}</span>
      </div>
      <strong style={{ ...styles.kpiValue, color }}>{value}</strong>
    </div>
  );
}

/* ================= TABLE BOX ================= */
function TableBox({ title, rows, onViewAll, viewAllLink }) {
  const navigate = useNavigate();

  return (
    <div
      style={styles.box}
      onMouseEnter={(e) =>
      (e.currentTarget.style.boxShadow =
        "0 12px 30px rgba(94,23,235,0.15)")
      }
      onMouseLeave={(e) =>
      (e.currentTarget.style.boxShadow =
        "0 4px 14px rgba(0,0,0,0.05)")
      }
    >
      <div style={styles.boxHeader}>
        <h3 style={styles.boxTitle}>{title}</h3>
        {viewAllLink && (
          <button
            onClick={() => navigate(viewAllLink)}
            style={styles.viewAllButton}
          >
            Voir tout →
          </button>
        )}
      </div>
      <table style={styles.table}>
        <tbody>
          {rows.length > 0 ? (
            rows.map((row, i) => (
              <tr
                key={i}
                style={styles.tableRow}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#f0ebff")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                {row.map((cell, j) => (
                  <td key={j} style={cell.style || {}}>
                    {cell.value}
                  </td>
                ))}
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="3" style={styles.emptyCell}>
                Aucune donnée disponible
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ================= DASHBOARD MANAGER ============== */
export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    reunionsPrevues: 0,
    reunionsRealisees: 0,
    tauxRealisation: "0%",
    tachesTotal: 0,
    tachesRetard: 0,
    comptesRendus: 0,
  });

  const [prochainesReunions, setProchainesReunions] = useState([]);
  const [tachesCritiques, setTachesCritiques] = useState([]);
  const [tachesParPriorite, setTachesParPriorite] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [authData, setAuthData] = useState({});

  useEffect(() => {
    setAuthData(getAuthData());
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const token = getToken();

      if (!token) {
        setError("Vous devez être connecté");
        setLoading(false);
        return;
      }

      // Fetch dashboard stats from backend
      const statsResponse = await fetch(`${API_URL}/dashboard/stats/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        console.log("Stats reçues:", statsData);

        setStats({
          reunionsPrevues: statsData.reunions_planifiees || 0,
          reunionsRealisees: statsData.reunions_terminees || 0,
          tauxRealisation: statsData.taux_realisation || "0%",
          tachesTotal: statsData.taches_total || 0,
          tachesRetard: statsData.taches_retard || 0,
          comptesRendus: statsData.comptes_rendus || 0,
        });

        // Mettre à jour les prochaines réunions
        if (statsData.prochaines_reunions && Array.isArray(statsData.prochaines_reunions)) {
          setProchainesReunions(statsData.prochaines_reunions.slice(0, 3));
        }
      }

      // Fetch all tasks for additional data
      const tasksResponse = await fetch(`${API_URL}/taches/`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (tasksResponse.ok) {
        const tasksData = await tasksResponse.json();
        let allTasks = [];

        if (tasksData.results && Array.isArray(tasksData.results)) {
          allTasks = tasksData.results;
        } else if (Array.isArray(tasksData)) {
          allTasks = tasksData;
        }

        console.log("Tâches récupérées:", allTasks.length);

        const now = new Date();

        // Tâches critiques (en retard ou haute priorité)
        const criticalTasks = allTasks
          .filter(t => t.statut !== 'terminee' && (
            t.priorite === 'critique' ||
            t.priorite === 'haute' ||
            (t.deadline && new Date(t.deadline) < now)
          ))
          .slice(0, 3)
          .map(t => ({
            description: t.description,
            responsable: t.responsable_nom || 'Non assigné',
            statut: t.deadline && new Date(t.deadline) < now ? 'En retard' :
              t.priorite === 'critique' ? 'Critique' : 'Urgent',
            id: t.id
          }));

        setTachesCritiques(criticalTasks);

        // Tâches par priorité
        const prioriteCount = {
          critique: allTasks.filter(t => t.priorite === 'critique' && t.statut !== 'terminee').length,
          haute: allTasks.filter(t => t.priorite === 'haute' && t.statut !== 'terminee').length,
          moyenne: allTasks.filter(t => t.priorite === 'moyenne' && t.statut !== 'terminee').length,
          basse: allTasks.filter(t => t.priorite === 'basse' && t.statut !== 'terminee').length
        };

        const tachesParPrioriteData = [
          { priorite: 'Critique', count: prioriteCount.critique, color: '#ef4444' },
          { priorite: 'Haute', count: prioriteCount.haute, color: '#f97316' },
          { priorite: 'Moyenne', count: prioriteCount.moyenne, color: '#eab308' },
          { priorite: 'Basse', count: prioriteCount.basse, color: '#16a34a' }
        ].filter(p => p.count > 0);

        setTachesParPriorite(tachesParPrioriteData);
      }

    } catch (error) {
      console.error("❌ Erreur chargement dashboard:", error);
      setError("Erreur de chargement des données");
    } finally {
      setLoading(false);
    }
  };

  // Format rows for the tables with click handlers
  const prochainesReunionsRows = prochainesReunions.map(r => [
    {
      value: <span style={styles.clickable} onClick={() => navigate(`/reunions/${r.id}`)}>{r.titre}</span>,
      style: { cursor: "pointer" }
    },
    { value: r.salle || 'Salle non définie' },
    { value: r.date_heure ? new Date(r.date_heure).toLocaleDateString('fr-FR') : 'Date non définie', style: styles.muted }
  ]);

  const tachesCritiquesRows = tachesCritiques.map(t => [
    {
      value: <span style={styles.clickable} onClick={() => navigate(`/taches/${t.id}`)}>{t.description}</span>,
      style: { cursor: "pointer" }
    },
    { value: t.responsable },
    {
      value: t.statut,
      style: t.statut === "En retard" ? styles.badge :
        t.statut === "Critique" ? styles.criticalBadge : styles.info
    }
  ]);

  const tachesParPrioriteRows = tachesParPriorite.map(p => [
    { value: p.priorite },
    { value: `${p.count} tâche${p.count > 1 ? 's' : ''}`, style: { fontWeight: 500 } },
    {
      value: (
        <span style={{
          ...styles.priorityBar,
          backgroundColor: p.color,
          width: `${Math.min(p.count * 20, 100)}%`
        }}></span>
      ),
      style: { width: '80px' }
    }
  ]);

  const icons = {
    reunionsPrevues: <FaCalendarAlt size={16} />,
    reunionsRealisees: <FaCheck size={16} />,
    tauxRealisation: <FaChartLine size={16} />,
    tachesTotal: <FaTasks size={16} />,
    tachesRetard: <FaExclamationCircle size={16} />,
    comptesRendus: <FaFileAlt size={16} />,
  };

  if (loading) {
    return (
      <Layout>
        <div style={styles.loadingContainer}>
          <div style={styles.spinner}></div>
          <p>Chargement du dashboard...</p>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <div style={styles.errorContainer}>
          <p style={{ color: "#ef4444" }}>❌ {error}</p>
          <button onClick={fetchDashboardData} style={styles.retryButton}>
            Réessayer
          </button>
        </div>
      </Layout>
    );
  }

  const role = authData?.role || "Manager";

  return (
    <Layout>
      <div style={styles.page}>
        <h1 style={styles.title}>
          Dashboard {role === 'Manager' ? 'Manager' : 'Administrateur'}
        </h1>
        <p style={styles.subtitle}>
          Pilotage global du projet PolyMeet
        </p>

        {/* KPI ROW */}
        <div style={styles.kpiRow}>
          <KpiCard
            label="Réunions prévues"
            value={stats.reunionsPrevues}
            color="#5e17eb"
            delay={100}
            icon={icons.reunionsPrevues}
            onClick={() => navigate('/listeReunions?statut=planifiee')}
          />
          <KpiCard
            label="Réunions réalisées"
            value={stats.reunionsRealisees}
            color="#22c55e"
            delay={200}
            icon={icons.reunionsRealisees}
            onClick={() => navigate('/listeReunions?statut=terminee')}
          />
          <KpiCard
            label="Taux de réalisation"
            value={stats.tauxRealisation}
            color="#0ea5e9"
            delay={300}
            icon={icons.tauxRealisation}
          />
          <KpiCard
            label="Tâches totales"
            value={stats.tachesTotal}
            color="#f59e0b"
            delay={400}
            icon={icons.tachesTotal}
            onClick={() => navigate('/taches')}
          />
          <KpiCard
            label="Tâches en retard"
            value={stats.tachesRetard}
            color="#ef4444"
            delay={500}
            icon={icons.tachesRetard}
            onClick={() => navigate('/taches?statut=retard')}
          />
          <KpiCard
            label="Comptes-rendus"
            value={stats.comptesRendus}
            color="#8b5cf6"
            delay={600}
            icon={icons.comptesRendus}
            onClick={() => navigate('/comptes-rendus')}
          />
        </div>

        {/* TABLES */}
        <div style={styles.tablesRow}>
          <TableBox
            title="Prochaines réunions"
            rows={prochainesReunionsRows}
            viewAllLink="/listeReunions?statut=planifiee"
          />

          <TableBox
            title="Tâches critiques"
            rows={tachesCritiquesRows}
            viewAllLink="/taches?priorite=critique,haute"
          />
        </div>

        <div style={{ ...styles.tablesRow, marginTop: "18px" }}>
          <TableBox
            title="Tâches par priorité"
            rows={tachesParPrioriteRows}
            viewAllLink="/taches"
          />

          <TableBox
            title="Statistiques"
            rows={[
              [
                { value: "Total réunions" },
                { value: stats.reunionsPrevues + stats.reunionsRealisees, style: { fontWeight: 500 } }
              ],
              [
                { value: "Taux complétion" },
                { value: stats.tauxRealisation, style: { color: "#0ea5e9", fontWeight: 500 } }
              ],
              [
                { value: "Tâches en cours" },
                { value: stats.tachesTotal - (stats.tachesRetard || 0), style: { color: "#f59e0b" } }
              ]
            ]}
            viewAllLink="/statistiques"
          />
        </div>
      </div>
    </Layout>
  );
}

/* ================= STYLES ================= */
const styles = {
  page: {
    background: "#f7f6ff",
    padding: "22px",
    borderRadius: "16px",
  },
  title: {
    fontSize: "26px",
    color: "#1f1f2e",
    fontWeight: "500",
    marginBottom: "4px",
  },
  subtitle: {
    color: "#666",
    marginBottom: "18px",
    fontSize: "14px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    minHeight: "400px",
    gap: "16px"
  },
  spinner: {
    width: "40px",
    height: "40px",
    border: "3px solid #f3f3f3",
    borderTop: "3px solid #5e17eb",
    borderRadius: "50%",
    animation: "spin 1s linear infinite"
  },
  errorContainer: {
    textAlign: "center",
    padding: "40px",
    background: "white",
    borderRadius: "12px"
  },
  retryButton: {
    marginTop: "12px",
    padding: "8px 16px",
    background: "#5e17eb",
    color: "white",
    border: "none",
    borderRadius: "8px",
    cursor: "pointer"
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(6, 1fr)",
    gap: "14px",
    marginBottom: "22px",
  },
  kpiCard: {
    background: "white",
    padding: "12px 14px",
    borderRadius: "10px",
    transition: "all 0.3s ease",
  },
  kpiHeader: {
    display: "flex",
    alignItems: "center",
  },
  kpiLabel: {
    fontSize: "12px",
    color: "#555",
  },
  kpiValue: {
    fontSize: "20px",
    display: "block",
    marginTop: "4px",
  },
  tablesRow: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "18px",
  },
  box: {
    background: "white",
    borderRadius: "14px",
    padding: "16px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.05)",
    transition: "all 0.3s ease",
  },
  boxHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "10px",
  },
  boxTitle: {
    fontSize: "15px",
    color: "#5e17eb",
    fontWeight: "600",
  },
  viewAllButton: {
    background: "transparent",
    border: "none",
    color: "#5e17eb",
    fontSize: "12px",
    cursor: "pointer",
    padding: "4px 8px",
    borderRadius: "4px",
    ":hover": {
      background: "#ede9ff"
    }
  },
  table: {
    width: "100%",
    fontSize: "13px",
  },
  tableRow: {
    transition: "background 0.25s ease",
  },
  clickable: {
    cursor: "pointer",
    color: "#1f1f2e",
    textDecoration: "none",
    ":hover": {
      color: "#5e17eb",
      textDecoration: "underline"
    }
  },
  muted: {
    color: "#888",
    textAlign: "right",
  },
  badge: {
    background: "#fee2e2",
    color: "#b91c1c",
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "8px",
    display: "inline-block",
  },
  criticalBadge: {
    background: "#ffedd5",
    color: "#f97316",
    fontSize: "11px",
    padding: "2px 8px",
    borderRadius: "8px",
    display: "inline-block",
  },
  priorityBar: {
    display: "block",
    height: "6px",
    borderRadius: "3px",
    transition: "width 0.3s ease",
  },
  ok: {
    color: "#16a34a",
    fontSize: "12px",
  },
  info: {
    color: "#0ea5e9",
    fontSize: "12px",
  },
  emptyCell: {
    textAlign: "center",
    color: "#9ca3af",
    padding: "20px 0",
  },
};

// Add spinner animation
const styleSheet = document.createElement("style");
styleSheet.innerText = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;
document.head.appendChild(styleSheet);